export const COLOUR_HEX = {
  red:    'd13434',
  purple: 'a834d1',
  blue:   '4334d1',
  green:  '178a0a',
  orange: 'f69635',
  yellow: 'fbdd1b',
  black:  '000000',
  white:  'ffffff',
  gold:   'd5bf75',
  silver: 'd6d6d6',
  brown:  '8d6746',
  pink:   'ef7cd4',
};

export const COLOUR_THRESHOLD = 100;

const hexToRgb = (hex) => {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

export const hexDistance = (a, b) => {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return Infinity;
  return Math.sqrt((ra[0] - rb[0]) ** 2 + (ra[1] - rb[1]) ** 2 + (ra[2] - rb[2]) ** 2);
};

/* ─────────────────────────────────────────────────────────────────────────────
   Manual colour-filter overrides (UAT – Colour Filter Adjustments).

   The automatic match (FilterPrimary/SecondaryColour, or hex distance when those
   are absent) puts some plates under the wrong colour — e.g. white plates under
   Silver, silver plates under Gold — and misses multi-colour plates such as AFL
   teams. These rules are applied on top of the automatic match:
     • remove → plate never appears under that colour (wins over add)
     • add    → plate always appears under that colour

   Rule fields (all matched case/punctuation-insensitively):
     product  – substring of ProductName ("custom" also matches "Motorcycle Custom",
                "afl" matches AFL Upgrade + AFL Premium)
     variants – exact colour DisplayName / theme name(s); omit to match every variant
     hex      – background hex; use instead of variants for "white background" rules
   ───────────────────────────────────────────────────────────────────────────── */
const AFL = 'afl';
export const COLOUR_FILTER_OVERRIDES = {
  red: {
    add: [
      { product: 'aussie', variants: ['Outback'] },
      { product: AFL, variants: ['Gold Coast Suns', 'Sydney Swans'] },
    ],
  },
  yellow: {
    add: [
      { product: 'custom', variants: ['Mellow Yellow on Black Tuxedo'] },
      { product: AFL, variants: ['Gold Coast Suns', 'Richmond Tigers', 'Hawthorn Hawks'] },
    ],
  },
  green: {
    add: [
      { product: 'custom', variants: ['Green on White'] },
      { product: 'style', variants: ['Green camouflage', 'Turquoise'] },
    ],
  },
  black: {
    add: [
      { product: 'style', variants: ['Paisley'] },
    ],
    remove: [
      { product: AFL, variants: ['Brisbane Lions', 'Fremantle Dockers', 'Hawthorn Hawks', 'Melbourne Demons'] },
    ],
  },
  orange: {
    add: [
      { product: 'aussie', variants: ['Outback'] },
    ],
  },
  blue: {
    add: [
      { product: 'custom', variants: ['Blue on White', 'In The Navy'] },
      { product: 'style', variants: ['Turquoise'] },
      { product: 'prestige', variants: ['Navy'] },
      { product: 'aussie', variants: ['Flag', 'Aussie Flag'] },
      { product: 'fishing', variants: ['White Angler'] },
      // PDF says "GI Black on White"; the only GI sample image is "Blue on White", so match either
      { product: 'general issue', variants: ['Black on White', 'Blue on White'] },
      { product: 'usa', variants: ['White with blue font', 'White with USA icon'] },
      { product: AFL, variants: ['Melbourne Demons', 'Adelaide Crows', 'North Melbourne Kangaroos', 'Port Adelaide Power', 'Western Bulldogs'] },
    ],
  },
  purple: {
    add: [
      { product: 'custom', variants: ['Lavender', 'Lavender on Black Tuxedo', 'Lavender on Phantom', 'Phantom', 'Phantom on White'] },
      { product: AFL, variants: ['Fremantle Dockers'] },
      { product: 'melbourne storm', variants: ['Purple'] },
    ],
  },
  brown: {
    add: [
      { product: AFL, variants: ['Hawthorn Hawks'] },
    ],
    remove: [
      { product: 'custom', variants: ['Granny Smith Green with White Font', 'Matte Grey', 'Matte Purple'] },
      { product: 'style', variants: ['Paisley'] },
    ],
  },
  gold: {
    add: [
      { product: 'custom', variants: ['Gold font on Black Tuxedo'] },
      { product: 'signature', variants: ['Black with Gold font'] },
    ],
    remove: [
      { product: 'custom', variants: ['Black Tuxedo font on Silver background'] },
      { product: 'style', variants: ['Chrome', 'Grey camouflage'] },
      { product: 'liquid metal' },
      { product: 'euro', variants: ['Black Tuxedo font on Silver background', 'Black Tuxedo on Silver'] },
      { product: 'signature', variants: ['Silver with Black font'] },
    ],
  },
  silver: {
    add: [
      { product: 'custom', variants: ['Matte Grey', 'Silver font on Black Tuxedo'] },
      { product: 'style', variants: ['Paisley'] },
      { product: 'signature', variants: ['Black with Silver font'] },
    ],
    remove: [
      { product: 'custom', variants: ['Black Tuxedo on White', 'Blue on White', 'Flamingo on White', 'Green on White', 'Phantom on White', 'Lavender'] },
      { product: 'deluxe', variants: ['White acrylic material'] },
      // "white background" rules: match by hex, and by known variant names in case the hex isn't exactly ffffff
      { product: 'japanese', hex: 'ffffff' },
      { product: 'japanese', variants: ['White', 'White with Black font', 'White with Green font'] },
      { product: 'usa', hex: 'ffffff' },
      { product: 'usa', variants: ['White with Black font', 'White with blue font', 'White with USA icon'] },
      { product: AFL },
      { product: 'fishing', variants: ['White Angler'] },
      { product: 'mustang', hex: 'ffffff' },
      { product: 'mustang', variants: ['White with Black font', 'Ford Mustang White with Black font', 'Black on white'] },
    ],
  },
};

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Every name a card's variant could be known by (colour key, theme, display name).
const cardVariantNames = (card) => {
  const v = (card.VariantsInBrowseStyle || card.VariantsInProduct || card.VariantsInProductRestyle || [])[0] || {};
  return [
    card._variantColour, card._variantTheme,
    v.ColourInfo?.DisplayName, v.ColourInfo?.Colour, v.ThemeInfo?.Theme, v.ThemeName,
  ].map(norm).filter(Boolean);
};

const ruleMatches = (rule, card) => {
  if (rule.product && !` ${norm(card.ProductName)} `.includes(` ${norm(rule.product)} `)) return false;
  if (rule.hex && norm(card._hexOuter) !== norm(rule.hex)) return false;
  if (rule.variants) {
    const names = cardVariantNames(card);
    if (!rule.variants.some((rv) => names.includes(norm(rv)))) return false;
  }
  return true;
};

// 'add' | 'remove' | null for this card under one colour
const overrideFor = (card, colourName) => {
  const o = COLOUR_FILTER_OVERRIDES[colourName];
  if (!o) return null;
  if ((o.remove || []).some((r) => ruleMatches(r, card))) return 'remove';
  if ((o.add || []).some((r) => ruleMatches(r, card))) return 'add';
  return null;
};

// Automatic match for one colour (named filter colours, else hex distance)
const autoMatchesColour = (card, n) => {
  const primary   = (card._filterPrimary   || '').toLowerCase();
  const secondary = (card._filterSecondary || '').toLowerCase();
  if (primary || secondary) return primary === n || secondary === n;

  if (!card._hexOuter) return false;
  const refHex = COLOUR_HEX[n];
  return refHex ? hexDistance(card._hexOuter, refHex) < COLOUR_THRESHOLD : false;
};

// Check if a card matches any of the selected colour names (overrides applied)
export const matchesColourFilter = (card, selectedColourNames) =>
  selectedColourNames.some((name) => {
    const n = name.toLowerCase();
    const o = overrideFor(card, n);
    if (o === 'remove') return false;
    if (o === 'add') return true;
    return autoMatchesColour(card, n);
  });

// Build the list of colour swatches to display — a colour is offered only if at
// least one card would match it, so the swatch list and the results stay in sync.
export const namedColoursForCards = (cards) =>
  Object.entries(COLOUR_HEX)
    .filter(([name]) => cards.some((card) => matchesColourFilter(card, [name])))
    .map(([name, hex]) => ({ name, hex }));
